import { FC } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useQuery, useQueryClient } from 'react-query'
import {
  AnexoType,
  ButtonType,
  downloadArquivo,
  ID,
  IconPaths,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  ButtonIcon,
  FormFileInput,
  FormInput,
  LoadingIndicator,
  useModal,
  useToast,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'
import { AnexoInterconsulta } from '../../core/_models'
import {
  baixarAnexoInterconsulta,
  enviarAnexoInterconsulta,
  getAnexosInterconsulta,
  removerAnexoInterconsulta,
} from '../../core/_requests'

type Props = {
  atendimentoId: ID
  /**
   * Pode anexar e remover: só o solicitante, com a interconsulta aguardando o teleconsultor (o
   * backend recusa com 422 fora disso). Sem ele, a lista é só para consultar e baixar.
   */
  editavel?: boolean
}

const schema = Yup.object().shape({
  arquivo: Yup.mixed().required('Selecione o arquivo'),
  descricao: Yup.string().max(50, 'Até 50 caracteres'),
})

/**
 * Anexos da solicitação (exames do paciente), pelas rotas próprias da interconsulta — as mesmas
 * para o solicitante (que anexa) e para o teleconsultor (que consulta).
 */
export const AnexosInterconsultaModal: FC<Props> = ({ atendimentoId, editavel = false }) => {
  const { closeModal } = useModal()
  const { t } = useT()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const chave = chaveAnexosInterconsulta(atendimentoId)

  const { data: todos = [], isLoading } = useQuery(
    chave,
    () => getAnexosInterconsulta(atendimentoId),
    { retry: false, onError: showError }
  )
  // Só exames: a rota devolve todos os anexos listáveis, e cada assinatura do parecer (inclusive a
  // da resposta a uma réplica) grava um "Formulário Assinado", que não é exame da solicitação.
  const anexos = todos.filter(isExame)
  // As escritas devolvem a lista inteira atualizada: substitui o cache em vez de buscar de novo.
  const atualizarLista = (lista: AnexoInterconsulta[]) => queryClient.setQueryData(chave, lista)

  const formik = useFormik({
    initialValues: { arquivo: null as File | null, descricao: '' },
    validationSchema: schema,
    onSubmit: async ({ arquivo, descricao }, { resetForm }) => {
      if (!arquivo) return
      try {
        atualizarLista(
          await enviarAnexoInterconsulta(atendimentoId, arquivo, descricao.trim() || undefined)
        )
        resetForm()
        showToast({ variant: 'success', title: 'Exame anexado.' })
      } catch (error) {
        showError(error)
      }
    },
  })

  const baixar = async (anexo: AnexoInterconsulta) => {
    try {
      await downloadArquivo(await baixarAnexoInterconsulta(atendimentoId, anexo.id))
    } catch (error) {
      showError(error)
    }
  }

  const remover = async (anexo: AnexoInterconsulta) => {
    const confirmado = await showConfirmAlert({
      title: 'Remover anexo?',
      text: `${nomeDoAnexo(anexo)} será removido da solicitação.`,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (!confirmado) return
    try {
      atualizarLista(await removerAnexoInterconsulta(atendimentoId, anexo.id))
    } catch (error) {
      showError(error)
    }
  }

  return (
    // Um formulário só, com as ações no rodapé como nos demais modais (Cancelar + Anexar). Em
    // leitura não há o que enviar: o rodapé fica só com Fechar.
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className='d-flex flex-column gap-6'
      aria-label='Exames da solicitação'
    >
      {editavel && (
        <section aria-labelledby='anexar-exame-titulo'>
          <h3 id='anexar-exame-titulo' className='fs-6 fw-bold mb-3'>
            Anexar exame
          </h3>
          <FormFileInput
            formik={formik}
            name='arquivo'
            label='Arquivo'
            className='mb-3'
            isRequired
            isDisabled={formik.isSubmitting}
          />
          <FormInput
            formik={formik}
            name='descricao'
            label='Descrição'
            placeholder='Ex.: Hemograma de 20/10'
            isDisabled={formik.isSubmitting}
          />
        </section>
      )}

      <section aria-labelledby='anexos-solicitacao-titulo'>
        <h3 id='anexos-solicitacao-titulo' className='fs-6 fw-bold mb-3'>
          Anexos da solicitação
        </h3>
        {isLoading ? (
          <p className='text-muted mb-0'>Carregando anexos...</p>
        ) : anexos.length === 0 ? (
          <p className='text-muted mb-0'>Nenhum exame anexado.</p>
        ) : (
          <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
            {anexos.map((anexo) => (
              <li
                key={anexo.id}
                className='d-flex align-items-center gap-3 border rounded px-3 py-2'
              >
                <div className='flex-grow-1 text-truncate'>
                  <div className='fw-semibold text-gray-900 text-truncate'>
                    {nomeDoAnexo(anexo)}
                  </div>
                  {anexo.dataFormatada && (
                    <div className='text-muted fs-7'>{anexo.dataFormatada}</div>
                  )}
                </div>
                <Button
                  variant='secondary-outline'
                  size='btn-sm'
                  icon={IconPaths.Download}
                  onClick={() => baixar(anexo)}
                  ariaLabel={`Baixar ${nomeDoAnexo(anexo)}`}
                >
                  Baixar
                </Button>
                {editavel && anexo.permiteExcluir !== false && (
                  <ButtonIcon
                    variant='secondary-outline-danger'
                    icon={IconPaths.Trash}
                    onClick={() => remover(anexo)}
                    ariaLabel={`Remover ${nomeDoAnexo(anexo)}`}
                    title='Remover'
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className='d-flex justify-content-center mt-4'>
        <Button
          type={ButtonType.Reset}
          className={`btn-secondary${editavel ? ' me-3' : ''}`}
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          {editavel ? t('common.cancel') : 'Fechar'}
        </Button>
        {editavel && (
          <Button
            type={ButtonType.Submit}
            className='btn-primary'
            icon={IconPaths.Add}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Anexar'}
          </Button>
        )}
      </div>
    </form>
  )
}

/**
 * Chave de cache dos anexos da interconsulta. Compartilhada com a tela do parecer: anexar aqui
 * atualiza a lista de exames de lá sem nova busca.
 */
export const chaveAnexosInterconsulta = (atendimentoId: ID) => [
  'interconsulta-anexos',
  atendimentoId,
]

export const isExame = (anexo: AnexoInterconsulta) => anexo.tipoAnexo?.id === AnexoType.Exame

export const nomeDoAnexo = (anexo: AnexoInterconsulta) =>
  anexo.descricao || anexo.fileName || `Anexo ${anexo.id}`
